import Link from "next/link";
import {
  GraduationCap,
  Check,
  ChevronRight,
  FileText,
  Video,
  Link2,
} from "lucide-react";

import { RuleHeader } from "@/components/home/HomePrimitives";
import LocalDueDate from "@/components/LocalDueDate";
import ModuleLaunch from "@/components/modules/ModuleLaunch";
import IconTile from "@/components/ui/icon-tile";
import { getModuleIcon } from "@/lib/modules/module-icons";
import { getNavIcon } from "@/components/nav/nav-icons";

// The nav's own Assignments icon, so a row and the menu draw the same thing.
const ASSIGNMENT_ICON = getNavIcon("/dashboard/assignments");

function PathRow({ path }) {
  const done = path.status === "complete";
  return (
    <Link
      href={path.href}
      className="group flex items-center gap-4 border-b border-gray-200/70 py-3.5 transition-colors last:border-b-0 hover:bg-black/1.5"
    >
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-transform duration-200 group-hover:scale-105 ${
          done
            ? "bg-emerald-50 text-emerald-600"
            : "bg-brand-50 text-brand-700"
        }`}
      >
        {done ? <Check className="h-4 w-4" /> : <GraduationCap className="h-4 w-4" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-semibold text-gray-900">
          {path.name}
        </span>
        <span className="truncate text-[13px] text-gray-500">
          {done ? "Completed" : path.nextStep || path.category || "Keep going"}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-3.5">
        {!done && <LocalDueDate value={path.dueDate} />}
        {done ? (
          <span className="w-9 text-right text-xs font-semibold text-emerald-600">
            100%
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <span className="hidden h-1.5 w-[72px] overflow-hidden rounded-full bg-gray-100 sm:flex">
              <span
                className="animate-grow-x h-full rounded-full bg-action-600"
                style={{
                  width: `${Math.min(100, Math.max(0, path.progress || 0))}%`,
                }}
              />
            </span>
            <span className="w-9 text-right text-xs font-semibold tabular-nums text-gray-900">
              {path.progress}%
            </span>
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-400" />
      </span>
    </Link>
  );
}

function ContinueLearning({ paths }) {
  if (!paths?.length) return null;
  const inProgress = paths.filter((p) => p.status !== "complete").length;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "220ms" }}>
      <RuleHeader
        title="Continue learning"
        meta={`${inProgress} in progress`}
        actionLabel="All paths"
        actionHref="/dashboard/learning-paths"
      />
      <div>
        {paths.map((path) => (
          <PathRow key={path.id} path={path} />
        ))}
      </div>
    </div>
  );
}

function ModulesRow({ modules }) {
  if (!modules?.length) return null;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "300ms" }}>
      <RuleHeader
        title="Jump into a session"
        actionLabel="All modules"
        actionHref="/simulator"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]">
        {modules.map((module) => (
          <ModuleLaunch
            key={module.id}
            size="compact"
            href={module.href}
            title={module.label}
            icon={getModuleIcon(module.icon)}
          />
        ))}
      </div>
    </div>
  );
}

function AssignmentRow({ item }) {
  return (
    <Link
      href={item.href}
      className="group flex items-center gap-4 border-b border-gray-200/70 py-3.5 transition-colors last:border-b-0 hover:bg-black/1.5"
    >
      <IconTile
        as="span"
        icon={ASSIGNMENT_ICON}
        size="md"
        tone="action"
        className="transition-transform duration-200 group-hover:scale-105"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-semibold text-gray-900">
          {item.title}
        </span>
        <span className="truncate text-[13px] text-gray-500">
          {item.typeLabel}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-3.5">
        <LocalDueDate value={item.dueDate} />
        <ChevronRight className="h-4 w-4 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-400" />
      </span>
    </Link>
  );
}

function PendingAssignments({ items }) {
  if (!items?.length) return null;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "360ms" }}>
      <RuleHeader
        title="Pending assignments"
        count={items.length}
        actionLabel="View all"
        actionHref="/dashboard?tab=assignments"
      />
      <div>
        {items.map((item) => (
          <AssignmentRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

// Tests only. Roleplay assignments have their own section above and their own
// page; this list is labelled and linked as tests so the two are never confused
// (a studio admin read "Due this week" as assignments and then found the
// Assignments page empty).
function TestsDue({ testsDue }) {
  const items = testsDue?.items;
  if (!items?.length) return null;
  return (
    <div className="animate-rise mb-6" style={{ "--rise-delay": "380ms" }}>
      <RuleHeader
        title="Tests due"
        count={testsDue.total ?? items.length}
        actionLabel="View all"
        actionHref="/dashboard/tests"
      />
      <div>
        {items.map((item) => (
          <Link
            key={item.id}
            href={item.href}
            className="group flex items-center gap-4 border-b border-gray-200/70 py-3.5 transition-colors last:border-b-0 hover:bg-black/1.5"
          >
            <span className="ml-1 h-[18px] w-[18px] shrink-0 rounded-full border-[1.6px] border-gray-300 transition-colors group-hover:border-brand-600" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-[15px] text-gray-900">
                {item.title}
              </span>
              <span className="truncate text-[12px] text-gray-500">
                {item.typeLabel}
              </span>
            </span>
            <LocalDueDate value={item.dueDate} className="shrink-0 text-[11.5px]" />
          </Link>
        ))}
      </div>
    </div>
  );
}

const RESOURCE_ICON = { video: Video, link: Link2, document: FileText };

export function FeaturedResources({ featured }) {
  if (!featured?.length) return null;
  return (
    <div className="animate-rise mb-8" style={{ "--rise-delay": "460ms" }}>
      <RuleHeader title="Featured resources" meta="featured by your admin" />
      <div>
        {featured.map((resource) => {
          const Icon = RESOURCE_ICON[resource.type] || FileText;
          // Opens beside the home page so the rep (or a trainer in a live
          // session) keeps their place and navigation.
          return (
            <Link
              key={resource.id}
              href={resource.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 border-b border-gray-200/70 py-3.5 transition-colors last:border-b-0 hover:bg-black/1.5"
            >
              <IconTile
                as="span"
                icon={Icon}
                size="md"
                tone="brand"
                className="transition-transform duration-200 group-hover:scale-105"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-semibold text-gray-900">
                  {resource.label}
                </span>
                <span className="truncate text-[13px] capitalize text-gray-500">
                  {[resource.type, resource.section].filter(Boolean).join(" · ")}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-400" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function RepDashboard({ data }) {
  return (
    <>
      <ContinueLearning paths={data.paths} />
      <ModulesRow modules={data.modules} />
      <PendingAssignments items={data.pendingAssignments} />
      <TestsDue testsDue={data.testsDue} />
    </>
  );
}
